<template>
    <!-- Non-modal from md up so another goal on the board can be picked while this one is open. -->
    <BaseDrawer
        ref="drawer"
        :open="!!goal"
        :title="shownGoal?.title ?? ''"
        title-voice="plain"
        wrap-title
        modal="mobile"
        :swipe-to-close="false"
        :drawer-content-class="isNestedDialogOpen ? 'bg-surface z-20' : 'bg-surface z-40'"
        @update:open="(open: boolean) => !open && emit('close')">
        <template v-if="shownGoal">
            <div class="px-6 py-5 flex flex-col gap-5">
                <!-- Date -->
                <div class="flex items-center gap-2">
                    <p class="body-small text-ink-muted">{{ t("journeys.goalDetailSidebar.addedOn", { date: fullDateLabel }) }}</p>
                </div>

                <!-- Description -->
                <div>
                    <p v-if="shownGoal.description" class="details-big text-ink">{{ shownGoal.description }}</p>
                    <p v-else class="details-big text-ink-muted">{{ t("journeys.goalDetailSidebar.noDescription") }}</p>
                </div>

                <!-- Actions -->
                <GoalActionsSection
                    ref="actionsRef"
                    :goal-id="shownGoal.id"
                    :actions="shownGoal.actions"
                    :allow-add-action="!disableActions"
                    @changed="emit('action-added', shownGoal.id)"
                    @all-completed="openCompletionConfirmation"
                    @request-delete-confirm="openActionDeletion" />
            </div>
        </template>
        <template v-if="shownGoal" #footer>
            <!-- Priority button -->
            <div v-if="showPriorityButton" class="px-6 pb-4">
                <GoalPriorityToggleButton :is-prioritized="!!shownGoal.prioritized_at" :disabled="isPriorityButtonDisabled" @click="emit('toggle-priority', shownGoal.id)" />
            </div>

            <!-- Standard footer -->
            <div v-if="!hideFooter" class="border-t border-border">
                <div class="flex items-center gap-3 px-6 py-4">
                    <BaseButton
                        v-if="isSuggestedGoal(shownGoal) && !shownGoal.team_member_goal && currentTab === 'for-you'"
                        theme="primary"
                        size="md"
                        class="md:hidden ml-auto"
                        @click="emit('track-suggestion', shownGoal.id)">
                        {{ t("journeys.goalDetailSidebar.trackThisGoal") }}
                    </BaseButton>
                    <BaseButton theme="secondary" size="md" :disabled="isFetchingChatUrl" @click="goToChat">
                        <span class="relative inline-flex items-center justify-center">
                            <LoadingSpinner v-if="isFetchingChatUrl" class="h-4 w-4 absolute" />
                            <span :class="isFetchingChatUrl ? 'invisible' : ''">{{ t("journeys.goalDetailSidebar.diveDeeper") }}</span>
                        </span>
                    </BaseButton>
                    <div class="ml-auto">
                        <BaseDropdownMenu :sections="moreMenuSections" side="top" :side-offset="4" @select="handleMoreMenuSelect" @close-auto-focus="handleMoreMenuCloseAutoFocus">
                            <template #trigger>
                                <BaseButton
                                    ref="moreMenuTrigger"
                                    theme="neutral"
                                    size="sm"
                                    icon-only
                                    :aria-label="t('journeys.goalDetailSidebar.moreActions')"
                                    @click="handleMoreMenuOpened">
                                    <i class="bi bi-three-dots text-sm" aria-hidden="true"></i>
                                </BaseButton>
                            </template>
                        </BaseDropdownMenu>
                    </div>
                </div>
            </div>

            <!-- Completed at banner -->
            <div v-if="shownGoal.completed_at" class="border-t border-border px-6 py-3 flex items-center gap-2">
                <i class="bi bi-check-circle-fill text-sm text-chip-status-done-text" aria-hidden="true"></i>
                <p class="body-small text-ink-muted">{{ t("journeys.goalDetailSidebar.completedOn", { date: completedAtLabel }) }}</p>
            </div>
        </template>
    </BaseDrawer>
    <template v-if="goal">
        <SidebarConfirmOverlay
            :open="showCompleteConfirm"
            :title="t('journeys.goalDetailSidebar.markCompleteTitle')"
            :confirm-text="t('journeys.goalDetailSidebar.completeGoal')"
            :is-loading="isCompleting"
            :error-text="completeErrorText"
            :return-focus-to="dialogReturnFocusTo"
            @update:open="handleCompleteOverlayVisibility"
            @confirmed="handleMarkComplete" />
        <BaseDialog
            width="narrow"
            :show-close="false"
            :open="connectionOverlayOpen"
            :title="t('journeys.assignGoalDialog.assignTitle')"
            :disable-close-on-backdrop-click="isAssigningConnection"
            :return-focus-to="dialogReturnFocusTo"
            mobile-height="expanded"
            @update:open="updateAssignmentOpen">
            <BaseNotification v-if="connectionAssignErrorText" theme="error" :text="connectionAssignErrorText" hide-dismiss />
            <ConnectionSearch ref="connectionSearchRef" auto-focus :disabled="isAssigningConnection" @select="selectedConnection = $event" @clear="selectedConnection = null" />
            <template #footer>
                <BaseButton theme="neutral" size="sm" :disabled="isAssigningConnection" @click="updateAssignmentOpen(false)">{{ t("common.cancel") }}</BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="!selectedConnection || isAssigningConnection" :aria-busy="isAssigningConnection" @click="submitConnectionAssign">
                    {{ isAssigningConnection ? t("common.saving") : t("common.save") }}
                </BaseButton>
            </template>
        </BaseDialog>
        <BaseDialog
            width="narrow"
            :show-close="false"
            :open="showDeleteFeedback"
            :title="t('journeys.goalDetailSidebar.deleteGoalTitle')"
            :disable-close-on-backdrop-click="isDiscarding"
            :return-focus-to="dialogReturnFocusTo"
            mobile-height="expanded"
            @update:open="updateDeletionOpen">
            <div class="flex flex-col gap-4">
                <BaseNotification v-if="deleteErrorText" theme="error" :text="deleteErrorText" hide-dismiss />
                <DiscardGoalFeedback
                    :goal-id="goal.id"
                    :is-deleting="isDeleting"
                    @update:processing="isSavingDiscardFeedback = $event"
                    @discarded="handleDelete"
                    @cancel="updateDeletionOpen(false)" />
            </div>
        </BaseDialog>
        <BaseDialog
            width="narrow"
            :show-close="false"
            :open="!!pendingDeleteActionId"
            :title="t('journeys.goalDetailSidebar.deleteActionTitle')"
            :description="t('journeys.goalDetailSidebar.deleteActionDescription')"
            :disable-close-on-backdrop-click="isDeletingAction"
            :return-focus-to="dialogReturnFocusTo"
            mobile-height="expanded"
            @update:open="updateActionDeletionOpen">
            <p class="body-small text-ink-muted">{{ t("journeys.goalDetailSidebar.deleteActionDescription") }}</p>
            <BaseNotification v-if="deleteActionErrorText" theme="error" :text="deleteActionErrorText" hide-dismiss />
            <template #footer>
                <BaseButton theme="neutral" size="sm" :disabled="isDeletingAction" @click="updateActionDeletionOpen(false)">{{ t("common.cancel") }}</BaseButton>
                <BaseButton theme="danger" size="sm" :disabled="isDeletingAction" :aria-busy="isDeletingAction" @click="handleConfirmDeleteAction">
                    {{ isDeletingAction ? t("journeys.goalDetailSidebar.deletingAction") : t("common.delete") }}
                </BaseButton>
            </template>
        </BaseDialog>
    </template>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { GoalUpdateRequest } from "~vue/api/generated/goals";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDrawer from "~vue/components/BaseDrawer.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type Goal } from "~vue/types/goals";
import { formatDate } from "~vue/utils/formatDate";
import { isSuggestedGoal } from "~vue/utils/goals";
import { computed, nextTick, ref, shallowRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import ConnectionSearch from "./ConnectionSearch.vue";
import DiscardGoalFeedback from "./DiscardGoalFeedback.vue";
import GoalActionsSection from "./GoalActionsSection.vue";
import GoalPriorityToggleButton from "./GoalPriorityToggleButton.vue";
import SidebarConfirmOverlay from "./SidebarConfirmOverlay.vue";
import { type DiscussWithNadiaClickedExtraData, JourneysAmplitudeEventNames, type JourneysTab, logJourneysUserInteraction } from "./utils";

const props = defineProps<{
    goal: Goal | null;
    canPrioritize?: boolean;
    hidePriorityButton?: boolean;
    hideFooter?: boolean;
    disableActions?: boolean;
    currentTab?: JourneysTab;
}>();

const emit = defineEmits<{
    close: [];
    "action-added": [goalId: string];
    "goal-completed": [goalId: string];
    "goal-deleted": [goalId: string];
    "goal-updated": [goalId: string];
    "goal-assigned": [goalId: string];
    "goal-confirmed": [goalId: string];
    "toggle-priority": [goalId: string];
    "track-suggestion": [goalId: string];
    edit: [goalId: string];
}>();

const { t } = useI18n();

const actionsRef = ref<InstanceType<typeof GoalActionsSection> | null>(null);
const pendingDeleteActionId = ref<string | null>(null);
const isDeletingAction = ref(false);
const hasDeleteActionError = ref(false);
const deleteActionErrorText = computed(() => (hasDeleteActionError.value ? t("journeys.goalDetailSidebar.deleteActionError") : ""));
const moreMenuTrigger = ref<InstanceType<typeof BaseButton> | null>(null);
const drawer = useTemplateRef<InstanceType<typeof BaseDrawer>>("drawer");
const dialogReturnFocusTo = shallowRef<HTMLElement | null>(null);

// The drawer keeps rendering the last goal through its exit slide, after `goal` is already null.
const shownGoal = shallowRef<Goal | null>(props.goal);
watch(
    () => props.goal,
    (goal) => {
        if (goal) {
            shownGoal.value = goal;
        }
    },
);

async function handleConfirmDeleteAction() {
    const actionId = pendingDeleteActionId.value;
    if (!actionId || isDeletingAction.value) {
        return;
    }
    isDeletingAction.value = true;
    hasDeleteActionError.value = false;
    try {
        await actionsRef.value?.executeDelete(actionId);
        dialogReturnFocusTo.value = drawer.value?.closeButton ?? null;
        pendingDeleteActionId.value = null;
    } catch {
        hasDeleteActionError.value = true;
        await nextTick();
        dialogReturnFocusTo.value = actionsRef.value?.$el.querySelector(
            `[data-goal-action-id="${CSS.escape(actionId)}"] button[aria-label="${t("journeys.goalActionsSection.moreActions")}"]`,
        );
    } finally {
        isDeletingAction.value = false;
    }
}

const showCompleteConfirm = ref(false);
const isCompleting = ref(false);
const showDeleteFeedback = ref(false);
const isDeleting = ref(false);
const isSavingDiscardFeedback = ref(false);
const isDiscarding = computed(() => isDeleting.value || isSavingDiscardFeedback.value);
const isNestedDialogOpen = computed(() => showCompleteConfirm.value || connectionOverlayOpen.value || showDeleteFeedback.value || !!pendingDeleteActionId.value);

function rememberDialogOpener() {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) {
        return;
    }
    const menu = activeElement.closest<HTMLElement>("[role=menu]");
    if (menu?.id) {
        const trigger = document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(menu.id)}"]`) ?? moreMenuTrigger.value?.$el;
        dialogReturnFocusTo.value = trigger instanceof HTMLElement ? trigger : null;
    } else if (!activeElement.closest("[data-dismissable-layer]")) {
        dialogReturnFocusTo.value = activeElement;
    } else if (!dialogReturnFocusTo.value?.isConnected) {
        const trigger = moreMenuTrigger.value?.$el;
        dialogReturnFocusTo.value = trigger instanceof HTMLElement ? trigger : null;
    }
}

function openCompletionConfirmation() {
    rememberDialogOpener();
    showCompleteConfirm.value = true;
}

function openActionDeletion(actionId: string) {
    rememberDialogOpener();
    hasDeleteActionError.value = false;
    pendingDeleteActionId.value = actionId;
}

function updateAssignmentOpen(value: boolean) {
    if (!isAssigningConnection.value) {
        connectionOverlayOpen.value = value;
    }
}

function updateDeletionOpen(value: boolean) {
    if (!isDiscarding.value) {
        showDeleteFeedback.value = value;
    }
}

function updateActionDeletionOpen(value: boolean) {
    if (!value && !isDeletingAction.value) {
        pendingDeleteActionId.value = null;
        hasDeleteActionError.value = false;
    }
}

const { execute: completeGoal, error: completeError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
)
    .patch({ status: "completed" })
    .json<Goal>();

const { execute: deleteGoal, error: deleteError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
).delete();

const hasCompleteError = ref(false);
const completeErrorText = computed(() => (hasCompleteError.value ? t("journeys.goalDetailSidebar.completeGoalError") : ""));
const hasDeleteError = ref(false);
const deleteErrorText = computed(() => (hasDeleteError.value ? t("journeys.goalDetailSidebar.deleteGoalError") : ""));

function handleCompleteOverlayVisibility(open: boolean) {
    if (!open && !isCompleting.value) {
        showCompleteConfirm.value = false;
        hasCompleteError.value = false;
    }
}

async function handleMarkComplete() {
    if (isCompleting.value) {
        return;
    }
    isCompleting.value = true;
    hasCompleteError.value = false;
    await completeGoal();
    isCompleting.value = false;

    if (completeError.value) {
        hasCompleteError.value = true;
        return;
    }

    showCompleteConfirm.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_COMPLETED, { goal_id: props.goal!.id, was_prioritized: !!props.goal!.prioritized_at }, props.currentTab);
    emit("goal-completed", props.goal!.id);
    emit("close");
}

async function handleDelete() {
    if (isDeleting.value) {
        return;
    }
    isDeleting.value = true;
    hasDeleteError.value = false;
    await deleteGoal();
    isDeleting.value = false;

    if (deleteError.value) {
        hasDeleteError.value = true;
        return;
    }

    showDeleteFeedback.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_DISCARDED, { goal_id: props.goal!.id }, props.currentTab);
    emit("goal-deleted", props.goal!.id);
    emit("close");
}

const connectionOverlayOpen = ref(false);
const connectionSearchRef = ref<InstanceType<typeof ConnectionSearch>>();
const selectedConnection = ref<{ id: string; name: string | null; job_title: string | null } | null>(null);
// Don't auto-open in track mode — the user clicks "Assign and track" instead
watch(
    () => props.goal?.id,
    () => {
        connectionOverlayOpen.value = !!props.goal?.team_member_goal && !props.goal?.connection;
    },
);

watch(connectionOverlayOpen, (open: boolean) => {
    if (!open) {
        selectedConnection.value = null;
        hasConnectionAssignError.value = false;
    }
});

const connectionPatch = ref<GoalUpdateRequest>({});
const { execute: patchConnection, error: patchConnectionError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
)
    .patch(connectionPatch)
    .json<Goal>();

const isAssigningConnection = ref(false);
const hasConnectionAssignError = ref(false);
const connectionAssignErrorText = computed(() => (hasConnectionAssignError.value ? t("journeys.assignGoalDialog.error") : ""));

async function submitConnectionAssign() {
    if (!selectedConnection.value || isAssigningConnection.value) {
        return;
    }
    connectionPatch.value = {
        connection_id: selectedConnection.value.id,
        team_member_goal: true,
        confirm_goal: true,
    };
    isAssigningConnection.value = true;
    hasConnectionAssignError.value = false;
    await patchConnection();
    isAssigningConnection.value = false;

    if (patchConnectionError.value) {
        hasConnectionAssignError.value = true;
        return;
    }

    connectionOverlayOpen.value = false;
    logJourneysUserInteraction(
        JourneysAmplitudeEventNames.GOAL_CONNECTION_ASSIGNED,
        {
            goal_id: props.goal!.id,
            is_new_assignment: !props.goal!.connection,
        },
        props.currentTab,
    );
    emit("goal-updated", props.goal!.id);
}

const {
    data: chatData,
    isFetching: isFetchingChatUrl,
    execute: fetchChatUrl,
} = useFetch(
    computed(() => `/goals/${props.goal?.id}/chat/`),
    { immediate: false },
).json<{ coaching_session_url: string }>();

function handleMoreMenuOpened() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_MORE_MENU_OPENED, { goal_id: props.goal?.id }, props.currentTab);
}

const moreMenuSections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (props.goal?.team_member_goal) {
        items.push({
            id: "assign",
            type: "button",
            label: props.goal.connection ? t("journeys.goalDetailSidebar.reassign") : t("journeys.goalDetailSidebar.assign"),
            icon: "bi bi-person",
        });
    }
    items.push(
        { id: "edit", type: "button", label: t("common.edit"), icon: "bi bi-pencil" },
        { id: "complete", type: "button", label: t("journeys.goalDetailSidebar.complete"), icon: "bi bi-check-circle" },
    );
    return [
        { id: "actions", items },
        { id: "danger", items: [{ id: "delete", type: "button", label: t("common.delete"), icon: "bi bi-trash" }] },
    ];
});

function handleMoreMenuSelect(item: DropdownMenuItem) {
    rememberDialogOpener();
    if (item.id === "assign") {
        connectionOverlayOpen.value = true;
    } else if (item.id === "edit") {
        handleEditClicked();
    } else if (item.id === "complete") {
        openCompletionConfirmation();
    } else if (item.id === "delete") {
        showDeleteFeedback.value = true;
    }
}

// A dialog opened from a menu must keep focus instead of returning it to the menu trigger.
// ConnectionSearch's auto-focus also needs to wait until the menu's focus scope is removed.
function handleMoreMenuCloseAutoFocus(event: Event) {
    if (!isNestedDialogOpen.value) {
        return;
    }
    event.preventDefault();
    if (connectionOverlayOpen.value) {
        globalThis.setTimeout(() => connectionSearchRef.value?.focus(), 0);
    }
}

function handleEditClicked() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_EDIT_CLICKED, { goal_id: props.goal!.id }, props.currentTab);
    emit("edit", props.goal!.id);
}

async function goToChat() {
    const discussData: DiscussWithNadiaClickedExtraData = { goal_id: props.goal?.id, source: "sidebar" };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.DISCUSS_WITH_NADIA_CLICKED, discussData, props.currentTab);
    await fetchChatUrl();
    if (chatData.value?.coaching_session_url) {
        globalThis.location.href = chatData.value.coaching_session_url;
    }
}

const showPriorityButton = computed(() => !props.hidePriorityButton && !shownGoal.value?.team_member_goal && !shownGoal.value?.completed_at);
const isPriorityButtonDisabled = computed(() => !shownGoal.value?.prioritized_at && !props.canPrioritize);

const fullDateLabel = computed(() => (shownGoal.value ? formatDate(shownGoal.value.reference_session_created_at ?? shownGoal.value.created_at, "date-long") : ""));

const completedAtLabel = computed(() => formatDate(shownGoal.value?.completed_at, "date-long"));
</script>
